import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Button } from '@/components/ui/button';
import { Share2, Download, X } from 'lucide-react';
import { toast } from 'sonner';
import sportaraLogo from '@/assets/sportara-logo-light.png';

export type MilestoneKind =
  | 'level-up'
  | 'module-complete'
  | 'streak'
  | 'first-lesson'
  | 'badge'
  | 'leaderboard-1';

export interface MilestoneEvent {
  id: string;            // dedup key
  kind: MilestoneKind;
  headline: string;
  subline: string;
  emoji: string;         // big icon
  xp: number;            // total xp to show in pill
  streakDays?: number;
}

interface MilestoneContextValue {
  enqueue: (e: MilestoneEvent) => void;
}

const MilestoneContext = createContext<MilestoneContextValue | null>(null);

export function useMilestoneQueue() {
  const ctx = useContext(MilestoneContext);
  if (!ctx) throw new Error('useMilestoneQueue must be inside MilestoneProvider');
  return ctx;
}

// Global helper so non-React modules (or hooks without context access)
// can enqueue milestones via window CustomEvent.
export function dispatchMilestone(e: MilestoneEvent) {
  window.dispatchEvent(new CustomEvent<MilestoneEvent>('sportara:milestone', { detail: e }));
}

// Brand palette
const CONFETTI_COLORS = ['#007bff', '#FFB020', '#1B2A4E', '#ffffff', '#FF7A45'];

function fireConfetti() {
  const defaults = { startVelocity: 35, spread: 360, ticks: 80, zIndex: 9999, colors: CONFETTI_COLORS };
  const end = Date.now() + 1200;
  (function frame() {
    confetti({ ...defaults, particleCount: 40, origin: { x: Math.random() * 0.3, y: Math.random() * 0.4 } });
    confetti({ ...defaults, particleCount: 40, origin: { x: 0.7 + Math.random() * 0.3, y: Math.random() * 0.4 } });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

function flameSize(days: number) {
  if (days >= 60) return 'text-[180px]';
  if (days >= 30) return 'text-[150px]';
  if (days >= 14) return 'text-[120px]';
  if (days >= 7) return 'text-[100px]';
  return 'text-[80px]';
}

interface OverlayProps {
  event: MilestoneEvent;
  onDismiss: () => void;
}

function CelebrationCard({ event, onDismiss }: OverlayProps) {
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    fireConfetti();
  }, [event.id]);

  const handleShare = async () => {
    const dataUrl = await buildShareImage(event);
    if (!dataUrl) return;
    // Try Web Share API first
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], 'sportara-achievement.png', { type: 'image/png' });
      const nav = navigator as Navigator & { canShare?: (data: { files: File[] }) => boolean; share?: (data: { files: File[]; title?: string }) => Promise<void> };
      if (nav.canShare?.({ files: [file] }) && nav.share) {
        await nav.share({ files: [file], title: 'My Sportara achievement' });
        return;
      }
    } catch { /* fall through to download */ }
    // Download fallback
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = 'sportara-achievement.png';
    a.click();
    toast.success('Achievement card downloaded!');
  };

  return (
    <motion.div
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4 overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, pointerEvents: 'none' }}
    >
      {/* Backdrop: dark navy + brand gradient */}
      <div className="absolute inset-0 bg-[#0A1428]/95 backdrop-blur-md" onClick={onDismiss} aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-br from-primary/25 via-transparent to-amber-500/15 animate-pulse pointer-events-none" />

      <motion.div
        key={event.id}
        className="relative w-full max-w-md rounded-3xl border border-white/10 bg-gradient-to-br from-[#0F1F3D]/90 to-[#0A1428]/95 shadow-2xl p-8 text-center"
        initial={{ scale: 0.7, y: 40, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 280, damping: 22 }}
      >
        <button
          onClick={onDismiss}
          aria-label="Close"
          className="absolute top-3 right-3 z-20 p-2.5 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition"
        >
          <X className="w-4 h-4" />
        </button>

        {!showShare ? (
          <>
            {/* Big icon — bouncing in from top */}
            <motion.div
              className="relative flex items-center justify-center mb-4"
              initial={{ y: -200, scale: 0.5 }}
              animate={{ y: 0, scale: 1 }}
              transition={{ type: 'spring', stiffness: 220, damping: 12, delay: 0.05 }}
            >
              {event.kind === 'streak' && event.streakDays ? (
                <div className="relative">
                  {/* Flame backdrop, sized by streak length */}
                  <div
                    className={`absolute inset-0 flex items-center justify-center ${flameSize(event.streakDays)} animate-pulse-flame`}
                    aria-hidden
                  >
                    🔥
                  </div>
                  <div className="relative display-number text-6xl text-white drop-shadow-[0_0_20px_rgba(255,176,32,0.8)]">
                    {event.streakDays}
                  </div>
                </div>
              ) : (
                <div className="text-8xl drop-shadow-[0_0_25px_rgba(0,123,255,0.6)]">{event.emoji}</div>
              )}
            </motion.div>

            <motion.h2
              className="font-display text-3xl font-bold text-white mb-2"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              {event.headline}
            </motion.h2>
            <motion.p
              className="text-white/70 font-body mb-5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {event.subline}
            </motion.p>

            {/* Glowing XP pill */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 ring-1 ring-primary/40 shadow-[0_0_30px_rgba(0,123,255,0.45)] mb-6"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5, type: 'spring' }}
            >
              <span className="text-lg">⚡</span>
              <span className="display-number text-lg text-white">{event.xp.toLocaleString()} XP</span>
            </motion.div>

            <div className="flex flex-col gap-2">
              <Button
                onClick={onDismiss}
                size="lg"
                className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground font-display font-bold"
              >
                Keep Going! →
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowShare(true)}
                className="w-full text-white/70 hover:text-white hover:bg-white/5"
              >
                <Share2 className="w-4 h-4 mr-2" /> Share your achievement
              </Button>
            </div>
          </>
        ) : (
          <SharePane event={event} onBack={() => setShowShare(false)} onShare={handleShare} />
        )}
      </motion.div>
    </motion.div>
  );
}

function SharePane({ event, onBack, onShare }: { event: MilestoneEvent; onBack: () => void; onShare: () => void }) {
  return (
    <div>
      <h3 className="font-display text-xl font-bold text-white mb-3">Share your moment</h3>
      <SharePreview event={event} />
      <div className="flex flex-col gap-2 mt-4">
        <Button onClick={onShare} size="lg" className="w-full bg-primary hover:bg-primary/90 font-display font-bold">
          <Download className="w-4 h-4 mr-2" /> Download / Share Image
        </Button>
        <Button variant="ghost" onClick={onBack} className="text-white/60 hover:text-white">
          Back
        </Button>
      </div>
    </div>
  );
}

function SharePreview({ event }: { event: MilestoneEvent }) {
  return (
    <div
      className="mx-auto rounded-2xl overflow-hidden ring-1 ring-white/10 relative"
      style={{ width: 280, height: 280, background: 'linear-gradient(135deg,#0A1428 0%,#1B2A4E 60%,#070E1F 100%)' }}
    >
      <div className="absolute inset-0 opacity-70" style={{ background: 'radial-gradient(circle at 50% 32%, rgba(0,123,255,0.35), transparent 60%)' }} />
      <div className="relative h-full flex flex-col items-center justify-center px-5 text-center">
        <div className="text-5xl mb-2 drop-shadow-[0_0_12px_rgba(0,123,255,0.6)]">{event.kind === 'streak' ? '🔥' : event.emoji}</div>
        <div className="font-display font-bold text-white text-lg leading-tight line-clamp-2 px-2">{event.headline}</div>
        <div className="text-white/70 text-[11px] font-body mt-1.5 line-clamp-2 px-3">{event.subline}</div>
        <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#007bff]/30 ring-1 ring-[#007bff]/60">
          <span className="text-sm">⚡</span>
          <span className="text-white text-sm font-display font-bold">{event.xp.toLocaleString()} XP</span>
        </div>
        <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-1.5">
          <img src={sportaraLogo} alt="" className="w-4 h-4 object-contain" />
          <span className="text-[9px] uppercase tracking-[0.3em] text-white/65 font-display font-bold">Sportara</span>
        </div>
      </div>
    </div>
  );
}

// Preload logo for the canvas (single image promise, reused).
let logoImgPromise: Promise<HTMLImageElement | null> | null = null;
function loadLogo(): Promise<HTMLImageElement | null> {
  if (logoImgPromise) return logoImgPromise;
  logoImgPromise = new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = sportaraLogo;
  });
  return logoImgPromise;
}

// Build a 1080x1080 PNG (rendered at 2x for retina-crisp text & emoji) for sharing.
async function buildShareImage(event: MilestoneEvent): Promise<string | null> {
  try {
    const size = 1080;
    const scale = 2; // supersample
    const canvas = document.createElement('canvas');
    canvas.width = size * scale;
    canvas.height = size * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.scale(scale, scale);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const displayFont = '"DM Sans", "Inter", system-ui, -apple-system, "Helvetica Neue", sans-serif';
    const emojiFont = '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", system-ui';

    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, size, size);
    grad.addColorStop(0, '#0A1428');
    grad.addColorStop(0.55, '#1B2A4E');
    grad.addColorStop(1, '#070E1F');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);

    // Accent glow
    const radial = ctx.createRadialGradient(size / 2, size * 0.32, 40, size / 2, size * 0.32, 620);
    radial.addColorStop(0, 'rgba(0,123,255,0.55)');
    radial.addColorStop(1, 'rgba(0,123,255,0)');
    ctx.fillStyle = radial;
    ctx.fillRect(0, 0, size, size);

    // Subtle border frame
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 2;
    roundRect(ctx, 36, 36, size - 72, size - 72, 48);
    ctx.stroke();

    ctx.textAlign = 'center';
    ctx.fillStyle = '#ffffff';

    // Icon / streak
    if (event.kind === 'streak' && event.streakDays) {
      ctx.font = `260px ${emojiFont}`;
      ctx.globalAlpha = 0.95;
      ctx.fillText('🔥', size / 2, size * 0.40);
      ctx.globalAlpha = 1;
      ctx.font = `bold 180px ${displayFont}`;
      ctx.shadowColor = 'rgba(255,176,32,0.85)';
      ctx.shadowBlur = 30;
      ctx.fillText(String(event.streakDays), size / 2, size * 0.42);
      ctx.shadowBlur = 0;
    } else {
      ctx.font = `260px ${emojiFont}`;
      ctx.fillText(event.emoji, size / 2, size * 0.40);
    }

    // Headline — auto-fits long player names / module titles up to 2 lines
    const headlineMax = size - 180;
    const headlineSize = fitFontSize(ctx, event.headline, headlineMax, 92, 52, `bold {SIZE}px ${displayFont}`, 2);
    ctx.font = `bold ${headlineSize}px ${displayFont}`;
    const headlineLines = wrapLines(ctx, event.headline, headlineMax, 2);
    const headlineLH = Math.round(headlineSize * 1.08);
    let hy = size * 0.56;
    ctx.fillStyle = '#ffffff';
    for (const line of headlineLines) {
      ctx.fillText(line, size / 2, hy);
      hy += headlineLH;
    }

    // Subline
    ctx.font = `400 36px ${displayFont}`;
    ctx.fillStyle = 'rgba(255,255,255,0.78)';
    wrapText(ctx, event.subline, size / 2, hy + 14, size - 220, 46, 2);

    // XP pill — width scales to fit text
    const pillLabel = `⚡  ${event.xp.toLocaleString()} XP`;
    ctx.font = `bold 48px ${displayFont}, ${emojiFont}`;
    const pillTextW = ctx.measureText(pillLabel).width;
    const pillW = Math.max(380, pillTextW + 80);
    const pillH = 96;
    const pillX = (size - pillW) / 2;
    const pillY = size * 0.80;
    const pillGrad = ctx.createLinearGradient(pillX, pillY, pillX + pillW, pillY);
    pillGrad.addColorStop(0, 'rgba(0,123,255,0.55)');
    pillGrad.addColorStop(1, 'rgba(0,123,255,0.25)');
    ctx.fillStyle = pillGrad;
    roundRect(ctx, pillX, pillY, pillW, pillH, 48);
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,123,255,0.8)';
    ctx.lineWidth = 2;
    roundRect(ctx, pillX, pillY, pillW, pillH, 48);
    ctx.stroke();
    ctx.fillStyle = '#ffffff';
    ctx.textBaseline = 'middle';
    ctx.fillText(pillLabel, size / 2, pillY + pillH / 2 + 2);
    ctx.textBaseline = 'alphabetic';

    // Branding lockup (logo + wordmark) bottom-center
    const logo = await loadLogo();
    ctx.font = `bold 30px ${displayFont}`;
    const wordmark = 'SPORTARA';
    const wmW = ctx.measureText(wordmark).width;
    const logoSize = 44;
    const gap = 14;
    const totalW = (logo ? logoSize + gap : 0) + wmW;
    const startX = (size - totalW) / 2;
    const brandY = size - 80;
    if (logo) ctx.drawImage(logo, startX, brandY - logoSize / 2 - 8, logoSize, logoSize);
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.textAlign = 'left';
    ctx.fillText(wordmark, startX + (logo ? logoSize + gap : 0), brandY);
    ctx.textAlign = 'center';

    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Shrink font size until text fits within maxWidth across <= maxLines lines.
function fitFontSize(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  startSize: number,
  minSize: number,
  fontTemplate: string,
  maxLines: number,
): number {
  for (let s = startSize; s >= minSize; s -= 4) {
    ctx.font = fontTemplate.replace('{SIZE}', String(s));
    const lines = wrapLines(ctx, text, maxWidth, maxLines + 1);
    if (lines.length <= maxLines) return s;
  }
  return minSize;
}

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  let i = 0;
  for (; i < words.length; i++) {
    const w = words[i];
    const test = line ? line + ' ' + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
      if (lines.length === maxLines) break;
    } else {
      line = test;
    }
  }
  if (lines.length < maxLines && line) lines.push(line);
  // Ellipsize if overflowed
  const overflowed = i < words.length - 1 || (lines.length === maxLines && line && lines[lines.length - 1] !== line);
  if (overflowed && lines.length) {
    let last = lines[lines.length - 1];
    while (last.length && ctx.measureText(last + '…').width > maxWidth) last = last.slice(0, -1);
    lines[lines.length - 1] = last.replace(/\s+$/, '') + '…';
  }
  return lines;
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  maxLines = 3,
) {
  const lines = wrapLines(ctx, text, maxWidth, maxLines);
  let cy = y;
  for (const line of lines) {
    ctx.fillText(line, x, cy);
    cy += lineHeight;
  }
}

export function MilestoneProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<MilestoneEvent[]>([]);
  const seenRef = useRef<Set<string>>(new Set());

  const enqueue = useCallback((e: MilestoneEvent) => {
    if (seenRef.current.has(e.id)) return;
    seenRef.current.add(e.id);
    setQueue(q => [...q, e]);
  }, []);

  // Listen to global dispatchMilestone events
  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<MilestoneEvent>).detail;
      if (detail) enqueue(detail);
    };
    window.addEventListener('sportara:milestone', handler);
    return () => window.removeEventListener('sportara:milestone', handler);
  }, [enqueue]);

  const current = queue[0];
  const dismiss = useCallback(() => {
    setQueue(q => q.slice(1));
  }, []);

  const value = useMemo(() => ({ enqueue }), [enqueue]);

  return (
    <MilestoneContext.Provider value={value}>
      {children}
      <AnimatePresence>
        {current && <CelebrationCard key={current.id} event={current} onDismiss={dismiss} />}
      </AnimatePresence>
    </MilestoneContext.Provider>
  );
}
